import { useEffect, useState } from "react";
import { ExternalLink, Newspaper, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Tape } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SEEN_KEY = "iym_feed_seen_at";

function FeedCard({ post, featured, isNew, onOpen }) {
  return (
    <button
      onClick={() => onOpen(post)}
      data-testid={`feed-card-${post.id}`}
      className={cn(
        "group relative shrink-0 snap-start overflow-hidden rounded-[20px] bg-white text-left ring-1 ring-[#1A3323]/10 transition-transform duration-300 hover:-translate-y-0.5",
        featured ? "w-full" : "w-[44%] min-w-[150px]"
      )}
    >
      <div className="relative aspect-square overflow-hidden bg-[#EFEBDD]">
        <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {isNew && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#1A3323] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#FDFCF8]" data-testid="feed-new-badge">
            <Sparkles className="h-3 w-3" /> Baru
          </span>
        )}
        {featured && <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#1A3323]/90 to-transparent" />}
        {featured && (
          <div className="absolute inset-x-0 bottom-0 p-4 text-[#FDFCF8]">
            <div className="text-[11px] font-semibold text-[#FDFCF8]/70">{fmtDate(post.created_at)}</div>
            <h3 className="mt-0.5 text-xl font-extrabold leading-tight">{post.title}</h3>
            {post.description && <p className="mt-1 line-clamp-2 text-sm text-[#FDFCF8]/80">{post.description}</p>}
          </div>
        )}
      </div>
      {!featured && (
        <div className="p-3">
          <h3 className="line-clamp-2 text-sm font-extrabold leading-snug text-[#1A3323]">{post.title}</h3>
          <div className="mt-1 text-[11px] text-muted-foreground">{fmtDate(post.created_at)}</div>
        </div>
      )}
    </button>
  );
}

function FeedDetail({ post, onOpenChange }) {
  return (
    <Dialog open={!!post} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto p-0 [&>button.absolute]:hidden" data-testid="feed-detail-dialog">
        {post && (
          <>
            <div className="relative">
              <img src={post.image} alt={post.title} className="aspect-square w-full rounded-t-lg object-cover" />
              <button onClick={() => onOpenChange(false)} aria-label="Tutup" className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#1A3323]/85 text-[#FDFCF8] shadow-lg backdrop-blur transition-transform hover:scale-105" data-testid="feed-detail-close-button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <DialogHeader className="text-left">
                <DialogTitle className="text-2xl font-extrabold text-[#1A3323]" data-testid="feed-detail-title">{post.title}</DialogTitle>
                <DialogDescription>{fmtDate(post.created_at)}</DialogDescription>
              </DialogHeader>
              {post.description && <p className="whitespace-pre-line text-sm leading-relaxed text-[#1A3323]/80" data-testid="feed-detail-description">{post.description}</p>}
              {post.link && (
                <a href={post.link} target="_blank" rel="noopener noreferrer" className="block pt-4" data-testid="feed-detail-link">
                  <Button className="h-12 w-full rounded-xl font-extrabold"><ExternalLink className="mr-2 h-4 w-4" /> Buka Link</Button>
                </a>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 w-full rounded-xl font-bold" data-testid="feed-detail-close-bottom">Tutup</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CommunityNewsFeed() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [seenAt] = useState(() => localStorage.getItem(SEEN_KEY) || "");
  useEffect(() => {
    api.get("/feed").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  const openPost = (post) => {
    setOpen(post);
    if (!localStorage.getItem(SEEN_KEY) || post.created_at > localStorage.getItem(SEEN_KEY)) {
      localStorage.setItem(SEEN_KEY, post.created_at);
    }
  };
  if (!items.length) return null;
  const [first, ...rest] = items;
  const isNew = (p) => !seenAt || p.created_at > seenAt;
  return (
    <section className="space-y-3 rise rise-2" data-testid="community-news-feed">
      <div className="flex items-end justify-between px-1">
        <div>
          <Tape>kabar komunitas</Tape>
          <h2 className="mt-2 flex items-center gap-2 text-xl font-extrabold text-[#1A3323]"><Newspaper className="h-5 w-5" /> Info Terbaru</h2>
        </div>
        <span className="text-xs font-semibold text-muted-foreground">{items.length} post</span>
      </div>
      <FeedCard post={first} featured isNew={isNew(first)} onOpen={openPost} />
      {rest.length > 0 && (
        <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1" data-testid="feed-carousel">
          {rest.map((p) => <FeedCard key={p.id} post={p} isNew={isNew(p)} onOpen={openPost} />)}
        </div>
      )}
      <FeedDetail post={open} onOpenChange={(o) => !o && setOpen(null)} />
    </section>
  );
}
